// Barra lateral para navegación secundaria y acciones rápidas
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  LayoutDashboard, Trophy, BarChart3, Users,
  Settings, Shield, ChevronLeft, ChevronRight,
  Layers, CalendarDays, Coins, Landmark, History, ArrowUpCircle, Megaphone,
  Tv, User, Receipt, KeyRound
} from 'lucide-react'
import { useState, useMemo } from 'react'
import useAuthStore from '../../store/useAuthStore'

// Enlaces del sidebar con agrupación
const sidebarLinks = [
  { group: 'Principal', items: [
    { path: '/dashboard', label: 'Panel', icon: LayoutDashboard },
    { path: '/ver-partido', label: 'Ver partido', icon: Tv },
    { path: '/apuestas', label: 'Apuestas', icon: Coins },
    { path: '/predicciones', label: 'Predicciones', icon: Trophy },
    { path: '/partidos', label: 'Partidos', icon: CalendarDays },
    { path: '/clasificacion', label: 'Clasificación', icon: BarChart3 },
    { path: '/ranking', label: 'Ranking', icon: BarChart3 },
    { path: '/comunidades', label: 'Comunidades', icon: Users },
    { path: '/perfil', label: 'Mi Perfil', icon: User },
  ]},
  { group: 'Créditos', items: [
    { path: '/movimientos', label: 'Movimientos', icon: History },
    { path: '/historial-billetera', label: 'Depósitos y Retiros', icon: Landmark },
  ]},
  { group: 'Análisis', items: [
    { path: '/estadisticas', label: 'Estadísticas', icon: BarChart3 },
  ]},
]

const adminSidebarLinks = [
  { group: 'Administración', items: [
    { path: '/admin?tab=dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/admin?tab=resultados', label: 'Marcadores', icon: Trophy },
    { path: '/admin?tab=partidos', label: 'Partidos', icon: CalendarDays },
    { path: '/admin?tab=equipos', label: 'Equipos', icon: Layers },
    { path: '/admin?tab=jugadores', label: 'Jugadores', icon: Users },
    { path: '/admin?tab=usuarios', label: 'Usuarios', icon: Shield },
    { path: '/admin?tab=permisos', label: 'Permisos', icon: KeyRound },
    { path: '/admin?tab=recargas', label: 'Recargas', icon: Landmark },
    { path: '/admin?tab=retiros', label: 'Retiros', icon: ArrowUpCircle },
    { path: '/admin?tab=finanzas', label: 'Finanzas', icon: Receipt },
    { path: '/admin?tab=apuestas', label: 'Apuestas', icon: Coins },
    { path: '/admin?tab=ajustes-saldo', label: 'Ajustes Saldo', icon: Landmark },
    { path: '/admin?tab=transmisiones', label: 'Transmisiones', icon: Tv },
    { path: '/admin?tab=publicidad', label: 'Publicidad', icon: Megaphone },
    { path: '/admin?tab=configuracion', label: 'Configuración', icon: Settings },
  ]},
  { group: 'Navegación', items: [
    { path: '/dashboard', label: 'Volver a la App', icon: LayoutDashboard },
  ]},
]

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const { user } = useAuthStore()
  const hasAdminAccess = user?.rol === 'admin' || user?.rol === 'superadmin' || (user?.permisos && Object.values(user.permisos).some(val => val === 1))
  const isAdminPath = location.pathname.startsWith('/admin')
  
  const activeSidebarLinks = useMemo(() => {
    if (isAdminPath) {
      // Mapeo de tabs a permisos
      const permissionMap = {
        'resultados': 'gestionar_resultados',
        'partidos': 'gestionar_partidos',
        'equipos': 'gestionar_equipos',
        'jugadores': 'gestionar_jugadores',
        'usuarios': 'gestionar_usuarios',
        'recargas': 'aprobar_recargas',
        'retiros': 'aprobar_retiros',
        'finanzas': 'gestionar_finanzas',
        'apuestas': 'gestionar_apuestas',
        'transmisiones': 'gestionar_canales',
        'publicidad': 'gestionar_publicidad',
        'configuracion': 'gestionar_configuracion',
        'ajustes-saldo': 'gestionar_finanzas',
      }

      let baseItems = [...adminSidebarLinks]
      if (user?.rol === 'superadmin' || user?.rol === 'admin') {
        baseItems = baseItems.map(group => {
          if (group.group === 'Administración') {
            if (group.items.some(item => item.path === '/admin?tab=historial-apuestas')) {
              return group
            }
            return {
              ...group,
              items: [
                ...group.items,
                { path: '/admin?tab=historial-apuestas', label: 'Historial Apuestas', icon: History }
              ]
            }
          }
          return group
        })
      }

      // Si el usuario no es admin ni superadmin (ej: empleado con algunos permisos), filtrar las opciones
      if (user?.rol !== 'admin' && user?.rol !== 'superadmin') {
        return baseItems.map(group => {
          if (group.group === 'Administración') {
            const filteredItems = group.items.filter(item => {
              // Siempre permitir Dashboard
              if (item.path.includes('tab=dashboard')) return true
              
              // Extraer el tab de la url
              const match = item.path.match(/tab=([^&]+)/)
              const tab = match ? match[1] : null
              
              if (tab === 'permisos') return false // Empleados nunca ven Permisos
              
              const requiredPermission = permissionMap[tab]
              return requiredPermission && user?.permisos && user.permisos[requiredPermission] === 1
            })

            // Agregar "Gastos y Caja" para el rol empleado
            if (user?.rol === 'empleado') {
              filteredItems.push({ path: '/finanzas-personal', label: 'Gastos y Caja', icon: Receipt })
            }

            return {
              ...group,
              items: filteredItems
            }
          }
          return group
        })
      }

      return baseItems
    }

    return sidebarLinks
  }, [isAdminPath, user])


  return (
    <aside
      className={`
        hidden lg:flex flex-col h-[calc(100vh-4rem)] sticky top-16
        glass border-r border-border-subtle
        transition-all duration-300
        ${collapsed ? 'w-16' : 'w-56'}
      `}
    >
      {/* Contenido del sidebar */}
      <div className="flex-1 py-4 overflow-y-auto">
        {activeSidebarLinks.map((group) => (
          <div key={group.group} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-2 text-[0.625rem] uppercase tracking-wider font-semibold text-text-muted font-body">
                {group.group}
              </p>
            )}
            <div className="space-y-0.5 px-2">
              {group.items.map(link => {
                const currentTab = new URLSearchParams(location.search).get('tab') || 'dashboard'
                const isActive = link.path.includes('tab=')
                  ? location.pathname.startsWith('/admin') && link.path.includes(`tab=${currentTab}`)
                  : location.pathname === link.path

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    title={collapsed ? link.label : undefined}
                    className={`
                      relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium font-body
                      transition-all duration-200
                      ${isActive
                        ? 'text-cyan bg-cyan/10'
                        : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                      }
                    `}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="sidebar-active"
                        className="absolute left-0 top-1 bottom-1 w-0.5 bg-cyan rounded-full"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                    <link.icon className="w-4 h-4 shrink-0" />
                    {!collapsed && <span>{link.label}</span>}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}

        {/* Enlace de administración (solo si no estamos en el panel de admin) */}
        {hasAdminAccess && !isAdminPath && (
          <div className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-2 text-[0.625rem] uppercase tracking-wider font-semibold text-text-muted font-body">
                Admin
              </p>
            )}
            <div className="px-2">
              <Link
                to="/admin"
                title={collapsed ? 'Administración' : undefined}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium font-body
                  transition-all duration-200
                  ${location.pathname === '/admin'
                    ? 'text-warning bg-warning/10'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
                  }
                `}
              >
                <Shield className="w-4 h-4 shrink-0" />
                {!collapsed && <span>Administración</span>}
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Botón de colapsar */}
      <div className="p-2 border-t border-border-subtle">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-card transition-colors"
          aria-label={collapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>
    </aside>
  )
}
